import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Calculator, ChevronDown, MessageSquarePlus, RotateCcw, Send, Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { cn } from '@/lib/cn';
import { formatDateTime } from '@/lib/dates';
import { Button, Card, CardContent, EmptyState, ErrorState, Textarea } from '@/features/common/ui';
import { WAITING_TEXT, type CfoSettings, type CfoMessage, type InstantIntent, type ThreadDetail, type ThreadList } from './types';

const PRIVACY_KEY = 'fint:cfo:ask:privacy-seen';
const POLL_MS = 8_000;

/**
 * Ask: a conversation with the books.
 *
 * The starter questions are answered at once by Fint, from its own figures.
 * Anything typed goes to the person's own Claude, which answers when it next
 * connects to Fint - the answer says it is waiting, in those words, and the
 * page checks every eight seconds while it does. Every amount an answer
 * quotes was checked against Fint's figures; one that was not is marked.
 */
export function AskTab() {
  const org = useActiveOrg();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const threadId = params.get('thread');
  const [text, setText] = useState('');
  const [privacySeen, setPrivacySeen] = useState(() => {
    try {
      return localStorage.getItem(PRIVACY_KEY) === '1';
    } catch {
      return false;
    }
  });

  const list = useQuery({
    queryKey: ['cfo-threads', org?.id],
    queryFn: () => api.get<ThreadList>('/cfo/threads'),
    enabled: !!org?.id,
    refetchInterval: (query) => (query.state.data?.threads.some((t) => t.waiting) ? POLL_MS : false),
  });
  const detail = useQuery({
    queryKey: ['cfo-thread', org?.id, threadId],
    queryFn: () => api.get<ThreadDetail>(`/cfo/threads/${threadId}`),
    enabled: !!org?.id && !!threadId,
    refetchInterval: (query) => (query.state.data?.messages.some((m) => m.status === 'waiting') ? POLL_MS : false),
  });

  const settings = useQuery({ queryKey: ['cfo-settings', org?.id], queryFn: () => api.get<CfoSettings>('/cfo/settings'), enabled: !!org?.id });
  const switchAsk = useMutation({
    mutationFn: (enabled: boolean) => api.patch('/cfo/settings', { ask: { enabled } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cfo-settings', org?.id] });
      void queryClient.invalidateQueries({ queryKey: ['cfo-threads', org?.id] });
    },
  });

  const open = (id: string | null) => {
    const copy = new URLSearchParams(params);
    if (id) copy.set('thread', id);
    else copy.delete('thread');
    setParams(copy, { replace: true });
  };
  const refresh = (data?: ThreadDetail) => {
    if (data) queryClient.setQueryData(['cfo-thread', org?.id, data.thread.id], data);
    void queryClient.invalidateQueries({ queryKey: ['cfo-threads', org?.id] });
  };

  const ask = useMutation({
    mutationFn: (body: { text?: string; intent?: InstantIntent }) => api.post<ThreadDetail>('/cfo/ask', { ...body, ...(threadId ? { threadId } : {}) }),
    onSuccess: (data) => {
      setText('');
      refresh(data);
      if (data.thread.id !== threadId) open(data.thread.id);
    },
  });
  const review = useMutation({
    mutationFn: () => api.post<ThreadDetail>('/cfo/review'),
    onSuccess: (data) => {
      refresh(data);
      open(data.thread.id);
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/cfo/threads/${id}`),
    onSuccess: () => {
      open(null);
      refresh();
    },
  });
  const retry = useMutation({
    mutationFn: (messageId: string) => api.post<ThreadDetail>(`/cfo/messages/${messageId}/retry`),
    onSuccess: (data) => refresh(data),
  });

  const seen = () => {
    setPrivacySeen(true);
    try {
      localStorage.setItem(PRIVACY_KEY, '1');
    } catch {
      // Shown again next visit; nothing worse.
    }
  };

  if (list.isError) return <Card><ErrorState error={list.error} onRetry={() => list.refetch()} /></Card>;
  const data = list.data;
  const thread = threadId ? detail.data : undefined;
  const askError = ask.error instanceof ApiError ? ask.error.message : null;

  return (
    <div className="grid gap-4 lg:grid-cols-[18rem_1fr]">
      <aside className={cn('space-y-3', threadId && 'hidden lg:block')} aria-label="Conversations">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => open(null)}>
            <MessageSquarePlus className="size-4" aria-hidden /> New question
          </Button>
          <Button size="sm" variant="ghost" loading={review.isPending} onClick={() => review.mutate()} data-testid="cfo-review-now">
            Last month&rsquo;s review
          </Button>
        </div>
        {!data ? (
          <div className="skeleton h-40 rounded-lg" />
        ) : data.threads.length === 0 ? (
          <p className="text-sm text-muted-foreground">No conversations yet.</p>
        ) : (
          <ul className="space-y-1">
            {data.threads.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => open(t.id)}
                  aria-current={t.id === threadId ? 'true' : undefined}
                  className={cn(
                    'w-full rounded-md px-3 py-2 text-left text-sm hover:bg-muted touch:min-h-11',
                    t.id === threadId && 'bg-primary/10 text-primary',
                  )}
                >
                  <span className="flex items-center gap-2 font-medium">
                    {t.kind === 'review' ? <Calculator className="size-3.5 shrink-0" aria-hidden /> : null}
                    <span className="truncate">{t.title}</span>
                    {t.waiting ? <span className="ml-auto size-2 shrink-0 rounded-full bg-warning" aria-label="Waiting for an answer" /> : null}
                  </span>
                  {t.last ? <span className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{t.last}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <section className="min-w-0 space-y-4" aria-label="Conversation">
        {threadId ? (
          <div className="flex items-center justify-between gap-2">
            <button type="button" className="text-sm text-primary hover:underline lg:hidden touch:min-h-11" onClick={() => open(null)}>
              ← All conversations
            </button>
            {thread?.thread.kind === 'ask' ? (
              <Button size="sm" variant="ghost" onClick={() => remove.mutate(thread.thread.id)} loading={remove.isPending}>
                <Trash2 className="size-4" aria-hidden /> Delete
              </Button>
            ) : null}
          </div>
        ) : null}

        {!privacySeen ? (
          <Card data-cfo-privacy>
            <CardContent className="space-y-2 pt-4 text-sm">
              <p>
                Questions you type are answered by <strong>your own Claude</strong>, connected to Fint. The question and a summary of the
                book&rsquo;s figures go to Anthropic as part of that work. Your questions are private to you: others with access to this
                book do not see them.
              </p>
              <Button size="sm" variant="outline" onClick={seen}>
                Understood
              </Button>
            </CardContent>
          </Card>
        ) : null}

        {threadId && detail.isError ? <Card><ErrorState error={detail.error} onRetry={() => detail.refetch()} /></Card> : null}
        {threadId && thread ? (
          <ol className="space-y-3" data-cfo-thread={thread.thread.id}>
            {thread.messages.map((m) => (
              <MessageBubble key={m.id} message={m} onRetry={() => retry.mutate(m.id)} retrying={retry.isPending} />
            ))}
          </ol>
        ) : null}
        {!threadId && data ? (
          <Card>
            <CardContent className="space-y-3 pt-4">
              <p className="text-sm font-medium">Answered at once, from your books:</p>
              <div className="flex flex-wrap gap-2">
                {data.starters.map((s) => (
                  <Button key={s.intent} size="sm" variant="outline" onClick={() => ask.mutate({ intent: s.intent })} disabled={ask.isPending} data-cfo-starter={s.intent}>
                    {s.question}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : null}

        {data && !data.askEnabled ? (
          <p className="text-sm text-muted-foreground">Asking Claude is switched off for this book; the starter questions still work.</p>
        ) : (
          <form
            className="space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (text.trim()) ask.mutate({ text: text.trim() });
            }}
          >
            <Textarea
              label={threadId ? 'Ask a follow-up' : 'Or ask anything'}
              rows={3}
              maxLength={2000}
              value={text}
              placeholder="Can I afford to hire someone at ₹40,000 a month?"
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && text.trim()) ask.mutate({ text: text.trim() });
              }}
            />
            {askError ? (
              <p role="alert" className="text-sm text-destructive">
                {askError}
              </p>
            ) : null}
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">Claude answers when it next connects to Fint.</p>
              <Button type="submit" size="sm" loading={ask.isPending} disabled={!text.trim()} data-testid="cfo-ask-send">
                <Send className="size-4" aria-hidden /> Ask
              </Button>
            </div>
          </form>
        )}
        {!threadId && data && data.threads.length === 0 ? (
          <EmptyState icon={Bot} title="Ask your books" description="Start with a question above. Answers show the figures they used, and link to the records." />
        ) : null}
        {settings.data?.canEdit ? (
          <label className="flex items-center gap-2 text-xs text-muted-foreground touch:min-h-11">
            <input type="checkbox" checked={settings.data.ask.enabled} onChange={(e) => switchAsk.mutate(e.target.checked)} />
            Let Claude answer typed questions in this book
          </label>
        ) : null}
      </section>
    </div>
  );
}

function MessageBubble({ message, onRetry, retrying }: { message: CfoMessage; onRetry: () => void; retrying: boolean }) {
  const [open, setOpen] = useState(false);
  const mine = message.role === 'user';
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (message.status === 'answered' && !mine) ref.current?.scrollIntoView?.({ block: 'nearest' });
  }, [message.status, mine]);

  return (
    <li ref={ref} className={cn('flex', mine ? 'justify-end' : 'justify-start')} data-cfo-message={message.role} data-status={message.status}>
      <div className={cn('max-w-[46rem] space-y-2 rounded-lg px-4 py-3 text-sm', mine ? 'bg-primary/10' : 'border border-border bg-card')}>
        {!mine ? (
          <p className="text-xs font-medium text-muted-foreground">
            {message.role === 'fint' ? 'Worked out by Fint' : 'Claude'} · {formatDateTime(message.createdAt)}
          </p>
        ) : null}
        {message.status === 'waiting' ? (
          <p className="text-muted-foreground" data-cfo-waiting>
            {WAITING_TEXT}
          </p>
        ) : message.status === 'failed' ? (
          <div className="space-y-2">
            <p className="text-destructive">Claude could not answer: {message.body}</p>
            <Button size="sm" variant="outline" onClick={onRetry} loading={retrying}>
              <RotateCcw className="size-4" aria-hidden /> Ask again
            </Button>
          </div>
        ) : (
          <p className="whitespace-pre-wrap leading-relaxed">{markUnchecked(message.body, message.unchecked)}</p>
        )}
        {message.unchecked.length ? (
          <p className="text-xs text-warning">Not checked against your books: {message.unchecked.join(', ')}</p>
        ) : null}
        {message.figures.length ? (
          <div>
            <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 text-xs text-primary hover:underline touch:min-h-11">
              See the numbers <ChevronDown className={cn('size-3 transition-transform', open && 'rotate-180')} aria-hidden />
            </button>
            {open ? (
              <dl className="mt-1 space-y-1 rounded-md bg-muted/50 px-3 py-2 text-xs">
                {message.figures.map((f) => (
                  <div key={`${f.source}:${f.label}`} className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">{f.label}</dt>
                    <dd className="font-medium tabular-nums">
                      {f.currency} {f.value}
                      {f.checked ? '' : ' (not checked)'}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </div>
        ) : null}
        {message.links.length || message.suggestions.length ? (
          <div className="flex flex-wrap gap-2 pt-1">
            {[...message.suggestions, ...message.links].map((l) => (
              <Button key={`${l.href}:${l.label}`} asChild size="sm" variant="outline">
                <Link to={l.href}>{l.label}</Link>
              </Button>
            ))}
          </div>
        ) : null}
      </div>
    </li>
  );
}

/** The answer's text with each unchecked amount marked, so a reader sees which figures Fint could not stand behind. */
function markUnchecked(body: string, unchecked: string[]): React.ReactNode {
  if (unchecked.length === 0) return body;
  const escaped = unchecked.map((u) => u.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const parts = body.split(new RegExp(`(${escaped.join('|')})`, 'g'));
  return parts.map((part, i) =>
    unchecked.includes(part) ? (
      <mark key={i} title="Not checked against your books" className="rounded bg-warning/20 px-0.5 text-foreground">
        {part}
      </mark>
    ) : (
      part
    ),
  );
}
